import { CustomField } from "@/Features/Acquisitions/Forms/hooks/useCustomFields";
import { z } from "zod";
import { isoDateRegex } from "./zod/regexHelpers";

/**
 * Resolves custom field validations, optionally filtering by evidence type
 * @param {Array} fields - Array of custom field objects
 * @param {number|null|undefined} currentEvidenceTypeId - Evidence type ID to filter by (optional)
 * @returns {Object} Zod validation schema object
 */
const resolveCustomFieldValidations = (
  fields: CustomField[],
  currentEvidenceTypeId: number | null | undefined = null
): Record<string, z.ZodTypeAny> => {
  // Filter fields to only include those that apply to the current evidence type
  const filteredFields = fields.filter((field: CustomField) => {
    // Include fields with empty type_ids (applies to all types)
    if (!field.type_ids || field.type_ids.length === 0) {
      return true;
    }

    // If no evidence type is selected, exclude fields that are specific to certain types
    if (currentEvidenceTypeId === null || currentEvidenceTypeId === undefined) {
      return false;
    }

    // Include fields where type_ids contains the current evidence type
    return field.type_ids.includes(currentEvidenceTypeId);
  });

  return filteredFields.reduce(
    (acc: Record<string, z.ZodTypeAny>, field: CustomField) => {
      const isRequired = field.is_required === 1;
      const fieldKey = `custom_attribute_${field.field_id}`;

      switch (field.editor_type) {
        case "textBox":
          return {
            ...acc,
            [fieldKey]: isRequired
              ? z
                  .string({
                    required_error: `${field.field_name} is required`,
                  })
                  .min(1, { message: `${field.field_name} is required` })
              : z.string().nullable().optional(),
          };
        case "dropDown":
          return {
            ...acc,
            [fieldKey]: isRequired
              ? z
                  .string({
                    required_error: `${field.field_name} is required`,
                  })
                  .min(1, { message: `${field.field_name} is required` })
              : z.string().nullable().optional(),
          };
        case "dateBox":
          return {
            ...acc,
            [fieldKey]: isRequired
              ? z
                  .string({
                    required_error: `${field.field_name} is required`,
                  })
                  .min(1, { message: `${field.field_name} is required` })
                  .regex(
                    isoDateRegex,
                    `Invalid date format. Please select a date or enter in correct date format.`
                  )
              : z
                  .string()
                  .regex(
                    isoDateRegex,
                    `Invalid date format. Please select a date or enter in correct date format.`
                  )
                  .nullable()
                  .optional(),
          };
        case "tagBox":
          return {
            ...acc,
            [fieldKey]: isRequired
              ? z
                  .array(z.string())
                  .min(1, { message: `${field.field_name} is required` })
              : z.array(z.string()).nullable().optional(),
          };
        default:
          return acc;
      }
    },
    {}
  );
};

export default resolveCustomFieldValidations;
